import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Disc3, Wrench, Shield, Truck, Star,
  CircleDot, Settings, ClipboardCheck, Car, ShoppingBag, Phone, MapPin, Mail, Building2, Clock,
} from "lucide-react";
import axios from "axios";
import ProductCard from "../components/ProductCard";
import Seo, { getSiteUrl } from "../components/Seo";
import { useShop } from "../context/ShopContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICES = [
  { icon: Disc3, title: "Pneumatici", desc: "Vendita e montaggio pneumatici auto, moto, truck, agro. Cambio stagionale con attrezzature di ultima generazione." },
  { icon: Settings, title: "Meccanica", desc: "Officina meccanica per diagnosi, manutenzione e riparazioni su tutti i veicoli." },
  { icon: ClipboardCheck, title: "Revisioni Ministeriali", desc: "Centro revisioni autorizzato per auto e veicoli commerciali." },
  { icon: Truck, title: "Officine Mobili", desc: "Servizio di assistenza e montaggio pneumatici direttamente dove serve a te." },
  { icon: CircleDot, title: "Rigenerazione Cerchi in Lega", desc: "Ripristino e rigenerazione professionale dei cerchi in lega." },
  { icon: Car, title: "Noleggio a Lungo Termine", desc: "Soluzioni di noleggio veicoli a lungo termine per privati e flotte." },
  { icon: Building2, title: "Leasing Company", desc: "Servizi dedicati alle leasing company e gestione flotte." },
  { icon: Shield, title: "Assistenza", desc: "Consulenza specializzata nella scelta del pneumatico ideale per le tue esigenze." },
];

const PUNTI_VENDITA = [
  { name: "Monterotondo", address: "Via E. Ramarini 27/29", phone: "06 900 41 28", email: "info@puntogommelazio.it" },
  { name: "Roma – Tiburtina", address: "Via di Salone, 172", phone: "06 41 90 530", email: "roma@puntogommelazio.it" },
  { name: "Mentana", address: "Via G. Spontini 57/59", phone: "06 906 97 35", email: "mentana@puntogommelazio.it" },
  { name: "Roma – Ponte Galeria", address: "Via di Malagrotta, 310", phone: "06 65 000 548", email: "malagrotta@puntogommelazio.it" },
  { name: "Roma – Trastevere", address: "Via A. Nobel 28/34", phone: "06 55 72 394", email: "marconi@puntogommelazio.it" },
];

const HIGHLIGHTS = [
  { value: "5", label: "sedi nel Lazio" },
  { value: "Auto", label: "moto, truck e agro" },
  { value: "Officina", label: "meccanica e revisioni" },
];

export default function Home() {
  const { salesEnabled } = useShop();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    if (!salesEnabled) {
      setFeatured([]);
      return;
    }
    axios
      .get(`${API_URL}/api/products`, { params: { featured: true, limit: 4 } })
      .then((res) => setFeatured(res.data.products || []))
      .catch(() => {});
  }, [salesEnabled]);

  useEffect(() => {
    if (!salesEnabled || !API_URL) return;
    const url = `${API_URL}/api/products?page=1&limit=20&sort=newest`;
    fetch(url, { method: "GET" }).catch(() => {});
  }, [salesEnabled]);

  const siteUrl = getSiteUrl();
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Puntogomme Lazio",
    url: siteUrl,
    logo: `${siteUrl}/logo192.png`,
    description: "Pneumatici, meccanica e revisioni nel Lazio. Vendita pneumatici auto, moto, truck, agro. Cambio gomme, revisioni ministeriali, officine mobili, noleggio e leasing.",
    email: "info@puntogommelazio.it",
    telephone: "+39069004128",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Via E. Ramarini 27/29",
      addressLocality: "Monterotondo",
      addressRegion: "RM",
      addressCountry: "IT",
    },
    sameAs: ["https://www.facebook.com/puntogommelazio", "https://www.instagram.com/puntogommelazio"],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Pneumatici e servizi auto",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Pneumatici" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Meccanica" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Revisioni ministeriali" } },
      ],
    },
    areaServed: PUNTI_VENDITA.map((p) => ({ "@type": "Place", name: p.name, address: { "@type": "PostalAddress", streetAddress: p.address } })),
  };

  const trust = [
    { icon: MapPin, title: "Cinque sedi", text: "Monterotondo, Roma e provincia" },
    { icon: Shield, title: "Garanzia", text: "Su pneumatici e lavorazioni" },
    salesEnabled
      ? { icon: Truck, title: "Spedizione", text: "Gratuita sopra i 200€" }
      : { icon: Wrench, title: "Officina", text: "Meccanica, gomme e revisioni" },
    { icon: Star, title: "Assistenza", text: "Ti aiutiamo a scegliere la misura giusta" },
  ];

  return (
    <main id="main-content" role="main">
      <Seo
        title="Pneumatici, meccanica e revisioni nel Lazio"
        description="Prenota online e scopri la vetrina digitale Puntogomme Lazio. Pneumatici auto, moto, truck, agro. Cambio gomme, meccanica, revisioni ministeriali. 5 punti vendita: Monterotondo, Roma Tiburtina, Mentana, Ponte Galeria, Trastevere."
        canonical="/"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden min-h-[640px] md:min-h-[760px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="/images/hero-background.webp"
            srcSet="/images/hero-background-640.webp 640w, /images/hero-background.webp 1024w"
            sizes="100vw"
            alt="Punto vendita Puntogomme Lazio"
            width={1024}
            height={546}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/45 to-white/5" />
          <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#F7F7F5] to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 pt-16 pb-28 md:pt-20 md:pb-32 w-full">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur-md border border-white px-3 py-1.5 shadow-sm mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-pg-red-dark" />
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-gray-700">
                Dal Lazio, per la tua auto
              </p>
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-gray-950 leading-[1.02] tracking-tight">
              Pneumatici, meccanica e revisioni.
            </h1>
            <p className="mt-5 text-base md:text-lg text-gray-700 leading-relaxed max-w-md">
              Cambio gomme, officina e revisioni ministeriali in cinque punti vendita. Qualità e professionalità, vicine a te.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#servizi" className="btn-primary">
                I nostri servizi <ArrowRight size={18} />
              </a>
              {salesEnabled ? (
                <Link to="/prodotti" className="btn-dark">
                  Vetrina digitale <ShoppingBag size={18} />
                </Link>
              ) : (
                <a href="tel:+39069004128" className="btn-dark">
                  Chiamaci <Phone size={18} />
                </a>
              )}
              <a href="#punti-vendita" className="btn-secondary">
                Punti vendita
              </a>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3 max-w-lg">
            {HIGHLIGHTS.map((item) => (
              <div key={item.label} className="rounded-2xl bg-white/75 backdrop-blur-md border border-white px-3 py-3 shadow-sm">
                <p className="text-2xl font-black text-gray-950 tabular-nums leading-none">{item.value}</p>
                <p className="text-[11px] text-gray-600 mt-1.5 leading-snug">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 max-w-7xl mx-auto px-4 -mt-14">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-100 rounded-3xl shadow-[0_20px_50px_-24px_rgba(0,0,0,0.35)] border border-gray-100 overflow-hidden">
          {trust.map((item) => (
            <div key={item.title} className="flex items-start gap-3 px-5 py-5 bg-white">
              <div className="w-10 h-10 rounded-xl bg-pg-red/15 flex items-center justify-center shrink-0">
                <item.icon size={18} className="text-pg-dark" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">{item.title}</p>
                <p className="text-xs text-pg-gray mt-0.5 leading-relaxed">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 pt-16 pb-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-800 mb-2">Da dove iniziare</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-950 tracking-tight">Come possiamo aiutarti</h2>
          </div>
          <p className="text-sm text-pg-gray max-w-sm leading-relaxed">
            {salesEnabled
              ? "Scegli i pneumatici online oppure passa in officina per meccanica, cambio gomme e revisioni."
              : "Meccanica, cambio gomme e revisioni nelle nostre sedi. Chiamaci e ti indichiamo quella più vicina."}
          </p>
        </div>

        <div className={`grid grid-cols-1 gap-4 ${salesEnabled ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
          {salesEnabled && (
            <Link
              to="/prodotti"
              className="group relative overflow-hidden rounded-3xl bg-pg-dark text-white p-7 md:col-span-1 min-h-[220px] flex flex-col justify-between"
            >
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-pg-red/20 blur-2xl" />
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-pg-red text-pg-dark flex items-center justify-center mb-5">
                  <ShoppingBag size={22} />
                </div>
                <h3 className="text-xl font-black tracking-tight">Vetrina digitale</h3>
                <p className="text-sm text-gray-400 mt-2 leading-relaxed">
                  Pneumatici auto, moto, truck e agro. Scegli online, montaggio e ritiro in sede.
                </p>
              </div>
              <span className="relative mt-6 inline-flex items-center gap-2 text-pg-red font-bold text-sm">
                Vai al catalogo <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          )}

          <a href="#servizi" className={`group bg-white rounded-3xl border border-gray-100 p-7 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all flex flex-col justify-between min-h-[220px] ${salesEnabled ? "" : "md:col-span-1"}`}>
            <div>
              <div className="w-12 h-12 rounded-2xl bg-pg-red/15 flex items-center justify-center mb-5">
                <Wrench size={22} className="text-pg-dark" />
              </div>
              <h3 className="text-xl font-black text-gray-950 tracking-tight">Officina</h3>
              <p className="text-sm text-pg-gray mt-2 leading-relaxed">
                Cambio gomme, meccanica, revisioni ministeriali, cerchi in lega e officine mobili.
              </p>
            </div>
            <span className="mt-6 inline-flex items-center gap-2 text-gray-900 font-bold text-sm">
              Scopri i servizi <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </a>

          <a href="#punti-vendita" className="group bg-white rounded-3xl border border-gray-100 p-7 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all flex flex-col justify-between min-h-[220px]">
            <div>
              <div className="w-12 h-12 rounded-2xl bg-pg-red/15 flex items-center justify-center mb-5">
                <MapPin size={22} className="text-pg-dark" />
              </div>
              <h3 className="text-xl font-black text-gray-950 tracking-tight">Cinque sedi</h3>
              <p className="text-sm text-pg-gray mt-2 leading-relaxed">
                Monterotondo, Tiburtina, Mentana, Ponte Galeria e Trastevere.
              </p>
            </div>
            <span className="mt-6 inline-flex items-center gap-2 text-gray-900 font-bold text-sm">
              Trova la più vicina <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </a>
        </div>
      </section>

      <section id="servizi" className="max-w-7xl mx-auto px-4 py-16 scroll-mt-20">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-800 mb-2">Officina</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-950 tracking-tight">Servizi per auto e pneumatici</h2>
            <p className="text-sm text-pg-gray mt-4 leading-relaxed">
              Controllo e manutenzione della tua auto, per sicurezza e prestazioni. Dalla gomma giusta alla revisione, nello stesso punto vendita.
            </p>
            <a href="tel:+39069004128" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-gray-900">
              <Phone size={16} className="text-pg-red-dark" /> 06 900 41 28
            </a>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
            {SERVICES.map((s) => (
              <div key={s.title} className="group bg-white rounded-2xl p-5 border border-gray-100 hover:border-pg-red/60 hover:shadow-md transition-all">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center shrink-0 group-hover:bg-pg-red transition-colors">
                    <s.icon size={18} className="text-gray-800 group-hover:text-pg-dark transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-950 text-sm leading-snug">{s.title}</h3>
                    <p className="text-xs text-pg-gray leading-relaxed mt-1.5">{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {salesEnabled && featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 pb-16">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-800 mb-2">Selezione</p>
              <h2 className="text-3xl font-black text-gray-950 tracking-tight">Alcune proposte per te</h2>
            </div>
            <Link to="/prodotti" className="btn-primary btn-sm shrink-0">
              Vedi tutti <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      <section id="punti-vendita" className="bg-white border-y border-gray-100 py-16 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-800 mb-2">Dove siamo</p>
              <h2 className="text-3xl md:text-4xl font-black text-gray-950 tracking-tight">I nostri punti vendita</h2>
            </div>
            <p className="text-sm text-pg-gray max-w-sm">
              Cinque sedi nel Lazio. Chiama la più vicina e ti aspettiamo.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PUNTI_VENDITA.map((pv) => (
              <article key={pv.name} className="rounded-3xl border border-gray-100 bg-[#F7F7F5] p-5 flex flex-col">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-2xl bg-white border border-gray-100 flex items-center justify-center">
                    <MapPin size={16} className="text-pg-dark" />
                  </div>
                  <h3 className="font-black text-gray-950">{pv.name}</h3>
                </div>
                <p className="text-sm text-pg-gray mb-5">{pv.address}</p>
                <div className="mt-auto flex flex-wrap gap-2">
                  <a href={`tel:${pv.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 rounded-full bg-pg-dark text-white text-xs font-bold px-3 py-2 hover:bg-pg-surface transition-colors">
                    <Phone size={12} /> {pv.phone}
                  </a>
                  <a href={`mailto:${pv.email}`} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-gray-200 text-xs font-semibold text-gray-700 px-3 py-2 hover:border-gray-300 transition-colors">
                    <Mail size={12} /> Email
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="contatti" className="max-w-7xl mx-auto px-4 py-16">
        <div className="relative overflow-hidden rounded-[2rem] bg-pg-dark text-white">
          <div
            className="absolute inset-0 opacity-80"
            style={{ backgroundImage: "radial-gradient(ellipse at 0% 100%, rgba(242,201,76,0.22) 0%, transparent 55%), radial-gradient(ellipse at 100% 0%, rgba(242,201,76,0.12) 0%, transparent 45%)" }}
          />
          <div className="relative grid lg:grid-cols-2 gap-10 p-8 md:p-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-pg-red mb-3">Parla con noi</p>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight">Ti aiutiamo a scegliere la gomma giusta.</h2>
              <p className="text-gray-400 mt-4 leading-relaxed max-w-md">
                Preventivi, montaggio e prenotazione in officina. Scrivici o chiama la sede di Monterotondo: ti indirizziamo noi.
              </p>
            </div>
            <div className="flex flex-col justify-center gap-3">
              <a href="tel:+39069004128" className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 px-5 py-4 hover:bg-white/10 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-pg-red text-pg-dark flex items-center justify-center shrink-0">
                  <Phone size={18} />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Telefono</p>
                  <p className="font-bold">06 900 41 28</p>
                </div>
              </a>
              <a href="mailto:info@puntogommelazio.it" className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 px-5 py-4 hover:bg-white/10 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <Mail size={18} className="text-pg-red" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Email</p>
                  <p className="font-bold">info@puntogommelazio.it</p>
                </div>
              </a>
              <div className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 px-5 py-4">
                <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <Clock size={18} className="text-pg-red" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Orari</p>
                  <p className="font-bold">Lun–Ven 8:30–18:30 · Sab 8:30–12:30</p>
                </div>
              </div>
              {salesEnabled && (
                <Link to="/prodotti" className="btn-primary mt-2 self-start">
                  Cerca pneumatici <ArrowRight size={16} />
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
